import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type TurPdfProfil } from '../api';
import { Alan, describe } from '../Workflow';
import { TUR_PDF as T } from '../metin/turPdf';
import { dosyaBase64 } from './mantik';

const DOSYA_LIMITI = 10 * 1024 * 1024;
const TOPLAM_LIMITI = 200 * 1024 * 1024;
type Satir = { file: File; id?: string; uploadAttempted?: boolean; output?: Blob; error?: string; state: 'bekliyor' | 'yukleniyor' | 'isleniyor' | 'basarili' | 'hatali' };
type Snapshot = { profileId: string; profileVersion: number; name: string };

// Ozgun ad korunur; yalniz ZIP yolu olabilecek karakterler temizlenir.
function guvenliZipAdi(ad: string) {
  const uzanti = /\.pdf$/i.exec(ad)?.[0] ?? '.pdf';
  const kok = ad.replace(/\.pdf$/i, '');
  const temiz = Array.from(kok).filter((char) => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127).join('').replace(/[\\/:*?"<>|]/g, '_').trim() || 'belge';
  return { kok: temiz, uzanti };
}

function zipAdlari(adlar: string[]): string[] {
  const temiz = adlar.map(guvenliZipAdi);
  // Anahtar normalize edilir; kullaniciya verilen ozgun Unicode ve harf bicimi degismez.
  const anahtar = (ad: string) => ad.normalize('NFKC').toUpperCase().toLowerCase().normalize('NFKC');
  const ayrilmis = new Set(temiz.map(({ kok, uzanti }) => anahtar(`${kok}${uzanti}`)));
  const kullanilan = new Set<string>();
  return temiz.map(({ kok, uzanti }) => {
    let aday = `${kok}${uzanti}`;
    if (kullanilan.has(anahtar(aday))) {
      let sira = 2;
      do { aday = `${kok} (${sira++})${uzanti}`; }
      while (ayrilmis.has(anahtar(aday)) || kullanilan.has(anahtar(aday)));
    }
    kullanilan.add(anahtar(aday));
    return aday;
  });
}

export function TopluPdf({ profil, busy, run, onUploaded, onProfileConflict }: {
  profil: TurPdfProfil | null;
  busy: boolean;
  onUploaded: () => Promise<void>;
  onProfileConflict: () => Promise<void>;
  run: (ad: string, is: () => Promise<string | void>) => Promise<void>;
}) {
  const [rows, setRows] = useState<Satir[]>([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [conflicted, setConflicted] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const alive = useRef(true);
  const abort = useRef<AbortController | null>(null);
  const worker = useRef<Worker | null>(null);
  const zipReject = useRef<((error: Error) => void) | null>(null);
  const url = useRef<string | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      abort.current?.abort();
      worker.current?.terminate();
      zipReject.current?.(new Error('Toplu PDF ekrani kapatildi.'));
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, []);

  const sec = (files: File[]) => {
    setError(null);
    if (files.length > 20) { setError(T.topluAdetSiniri); return; }
    if (files.some((file) => file.size > DOSYA_LIMITI)) { setError(T.topluBoyutSiniri); return; }
    if (files.reduce((n, file) => n + file.size, 0) > TOPLAM_LIMITI) { setError(T.topluToplamSiniri); return; }
    if (files.some((file) => !/\.pdf$/i.test(file.name))) { setError(T.topluPdfGerekli); return; }
    if (url.current) { URL.revokeObjectURL(url.current); url.current = null; }
    setRows(files.map((file) => ({ file, state: 'bekliyor' })));
    setSnapshot(null); setProgress(null); setConflicted(false);
  };
  const changed = snapshot !== null && (profil?.id !== snapshot.profileId || profil?.version !== snapshot.profileVersion);
  const engel = !profil ? T.engelProfil : profil.rules.length === 0 ? T.engelKuralYok : changed || conflicted ? T.topluProfilDegisti : rows.length === 0 ? T.topluDosyaSec : null;
  const basarili = rows.filter((row) => row.output).length;
  const tekrar = rows.some((row) => row.state === 'hatali' && row.id);

  const isle = () => run('toplu-pdf', async () => {
    if (engel || !profil) return;
    const sabit = snapshot ?? { profileId: profil.id, profileVersion: profil.version, name: profil.name };
    setSnapshot(sabit); setError(null);
    const liste = rows.map((row) => ({ ...row }));
    const controller = new AbortController();
    abort.current = controller;
    let uploaded = false;
    let toplam = liste.reduce((n, row) => n + (row.output?.size ?? 0), 0);
    const goster = () => { if (alive.current) setRows(liste.map((row) => ({ ...row }))); };
    for (let i = 0; i < liste.length; i++) {
      const row = liste[i]!;
      if (!alive.current) return;
      // Yukleme yaniti kaybolduysa belge zaten kaydedilmis olabilir. Otomatik ikinci upload yok.
      if (row.output || (!row.id && row.uploadAttempted)) continue;
      setProgress(T.topluIlerleme(i + 1, liste.length));
      try {
        if (!row.id) {
          row.state = 'yukleniyor'; row.error = undefined; goster();
          const bytes = await row.file.arrayBuffer();
          if (!alive.current) return;
          row.uploadAttempted = true;
          const source = await api.turPdfKaynakYukle({ fileName: row.file.name, contentBase64: dosyaBase64(bytes) }, controller.signal);
          row.id = source.id; uploaded = true;
        }
        if (!alive.current) return;
        row.state = 'isleniyor'; row.error = undefined; goster();
        const result = await api.turPdfIsle({ documentId: row.id, profileId: sabit.profileId, profileVersion: sabit.profileVersion, ...(password ? { password } : {}) }, controller.signal);
        if (!alive.current) return;
        if (toplam + result.blob.size > TOPLAM_LIMITI) throw new Error(T.topluCiktiSiniri);
        toplam += result.blob.size;
        row.output = result.blob; row.state = 'basarili';
      } catch (err) {
        if (!alive.current) return;
        row.state = 'hatali';
        row.error = row.id ? describe(err) : T.topluYuklemeBelirsiz;
        if (err instanceof ApiError && err.status === 409) {
          setConflicted(true); setError(T.topluProfilDegisti); goster();
          await onProfileConflict();
          break;
        }
      }
      goster();
    }
    if (alive.current) setProgress(T.topluSonuc(liste.filter((row) => row.output).length, liste.length));
    abort.current = null;
    if (uploaded && alive.current) await onUploaded();
  });

  const indir = () => run('toplu-zip', async () => {
    if (!basarili) return;
    const ciktilar = rows.flatMap((row) => row.output ? [{ name: row.file.name, blob: row.output }] : []);
    const adlar = zipAdlari(ciktilar.map((row) => row.name));
    const entries = ciktilar.map((row, i) => ({ name: adlar[i]!, blob: row.blob }));
    const zip = await new Promise<Blob>((resolve, reject) => {
      const task = new Worker(new URL('./zip.worker.ts', import.meta.url), { type: 'module' });
      worker.current = task; zipReject.current = reject;
      const temizle = () => { task.terminate(); worker.current = null; zipReject.current = null; };
      task.onmessage = (event: MessageEvent<{ blob?: Blob; error?: string }>) => {
        temizle();
        if (event.data.blob) resolve(event.data.blob); else reject(new Error(T.topluZipHatasi));
      };
      task.onerror = () => { temizle(); reject(new Error(T.topluZipHatasi)); };
      task.postMessage(entries);
    });
    if (!alive.current) return;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = URL.createObjectURL(zip);
    const a = document.createElement('a'); a.href = url.current; a.download = 'duzenlenmis-pdfler.zip';
    document.body.append(a); a.click(); a.remove();
  });

  return <div className="tur-pdf-bolum" data-testid="tur-pdf-batch">
    <h4>{T.topluBaslik}</h4>
    <p className="tur-pdf-aciklama">{T.topluAciklama}</p>
    <Alan etiket={T.topluDosyalar} ipucu={T.topluSinirlar}>
      <input type="file" multiple accept="application/pdf" disabled={busy} data-testid="tur-pdf-batch-upload"
        onChange={(event) => { sec(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
    </Alan>
    <Alan etiket={T.topluParola} ipucu={T.topluParolaIpucu}>
      <input type="password" autoComplete="off" disabled={busy} value={password} onChange={(event) => setPassword(event.target.value)} data-testid="tur-pdf-batch-password" />
    </Alan>
    {snapshot ? <p className="ops-document-hint">{T.topluProfil(snapshot.name, snapshot.profileVersion)}</p> : null}
    {engel ? <p role="status" className="ops-document-hint" data-testid="tur-pdf-batch-blocked">{engel}</p> : null}
    {error ? <p role="alert" className="callout callout-error" data-testid="tur-pdf-batch-error">{error}</p> : null}
    {progress ? <p role="status" aria-live="polite" data-testid="tur-pdf-batch-progress">{progress}</p> : null}
    {rows.length > 0 ? <ul className="tur-pdf-liste" data-testid="tur-pdf-batch-results">{rows.map((row, i) => <li key={i}>
      <span><strong>{row.file.name}</strong><small>{T.topluDurum[row.state]}</small>{row.error ? <small>{row.error}</small> : null}</span>
    </li>)}</ul> : null}
    <div className="tur-pdf-eylemler">
      <button type="button" className="birincil" data-testid="tur-pdf-batch-process" disabled={busy || engel !== null || (snapshot !== null && !tekrar)} onClick={() => void isle()}>{snapshot ? T.topluTekrar : T.topluIsle}</button>
      <button type="button" className="ikincil" data-testid="tur-pdf-batch-download" disabled={busy || basarili === 0} onClick={() => void indir()}>{T.topluZip(basarili)}</button>
    </div>
  </div>;
}
